import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { EditPagesResponse } from '@rossum/api-client/annotations';
import { Queue } from '@rossum/api-client/queues';
import { isEmbedded } from '../../../constants/config';
import { constructDocumentUrl, getIDFromString } from '../../../lib/url';
import { getAnnotationBacklink } from '../../../routes/useAnnotationBacklink';
import {
  EditState,
  extractBackendErrorMessage,
  humanizePageIdsInMessage,
} from '../../document-edit-core';
import { notify } from '../../toaster';

export type UseSplitConfirmNavigationResult = {
  onConfirmSuccess: (
    response: EditPagesResponse,
    currentQueue: Queue | undefined
  ) => void;
  onConfirmError: (error: unknown, editState: EditState) => void;
};

export const useSplitConfirmNavigation =
  (): UseSplitConfirmNavigationResult => {
    const intl = useIntl();
    const history = useHistory();

    const onConfirmSuccess: UseSplitConfirmNavigationResult['onConfirmSuccess'] =
      (response, currentQueue) => {
        // This can happen if you delete all pages.
        if (!isEmbedded() && response.results.length === 0) {
          history.replace(getAnnotationBacklink());
          return;
        }

        const confirmAction =
          currentQueue?.settings.uiOnEditConfirm ?? 'validate_first';

        switch (confirmAction) {
          case 'validate_first':
            history.replace(
              constructDocumentUrl({
                id: getIDFromString(response.results[0]?.annotation),
              })
            );
            break;
          // TODO(MAT-1968): `edit_next` should open the next annotable annotation;
          // until then it falls back to the annotation list, like unknown values.
          case 'edit_next':
          case 'annotation_list':
          default:
            history.replace(getAnnotationBacklink());
            break;
        }

        // TODO(MAT-1966): the embedded `return_url` redirect (legacy
        // `confirmEditModeFulfilled`) is out of scope on this branch — MAT-1966
        // stacks on top and plugs into this same `onConfirmSuccess` contract.
      };

    const onConfirmError = (error: unknown, editState: EditState) => {
      const backendMessage = extractBackendErrorMessage(error);
      if (backendMessage) {
        notify.error({
          title: humanizePageIdsInMessage(backendMessage, editState),
        });
      } else {
        notify.error({
          title: intl.formatMessage({ id: 'containers.message.clientError' }),
        });
      }
    };

    return { onConfirmSuccess, onConfirmError };
  };
